import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faExpand, faHeartbeat } from "@fortawesome/free-solid-svg-icons";
import Icon3D from "../components/ui/icon-3d";
import { TONES } from "../components/ui/tones";
import useAutoRefresh from "../hooks/use-auto-refresh";
import { Overview, fetchOverview } from "../utils/api";
import { CHECK_LOOK, HEALTH_LOOK, PLATFORM_LOOK } from "../utils/status";
import { CheckStatus } from "../types/health-check";

// ----------------------- LIVE CLOCK ------------------------------------
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

export default function StatusWallPage() {
  const now = useClock();
  const [data, setData] = useState<Overview | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchOverview());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, 30000);

  const bad = data?.platforms.filter((item) => item.status !== "healthy").length ?? 0;

  return (
    <div className="dark min-h-screen bg-[#060914] p-6 text-white lg:p-10">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(1000px_500px_at_15%_-10%,rgba(99,102,241,0.25),transparent_60%),radial-gradient(800px_500px_at_100%_100%,rgba(14,165,233,0.15),transparent_60%)]" />
      <header className="relative mb-8 flex flex-wrap items-center gap-4">
        <Icon3D icon={faHeartbeat} tone="indigo" size="xl" />
        <div>
          <p className="font-[Gilroy-Bold] text-[32px] leading-none lg:text-[40px]">HealthSure</p>
          <p className="mt-1 font-[Gilroy-Medium] text-[15px] text-white/60">Sure platforms · production · refreshes every 30s{failed ? " · last refresh failed" : ""}</p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <span className="font-mono text-[40px] font-semibold tabular-nums lg:text-[52px]">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
          <button type="button" aria-label="Full screen" onClick={() => document.documentElement.requestFullscreen?.()} className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 transition-colors hover:bg-white/20">
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <Link to="/" aria-label="Back to the dashboard" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 transition-colors hover:bg-white/20">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
        </div>
      </header>

      <div className={clsx("relative mb-8 rounded-3xl px-8 py-6 font-[Gilroy-Bold] text-[26px] lg:text-[34px]", bad ? "bg-gradient-to-r from-rose-600 to-orange-500" : "bg-gradient-to-r from-emerald-500 to-sky-500")}>
        {!data ? "Loading…" : bad ? `${bad} platform${bad > 1 ? "s" : ""} need attention · ${data.totals.openAlerts} open alerts` : `All ${data.platforms.length} platforms healthy`}
      </div>

      <div className="relative grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {data?.platforms.map((item) => {
          const look = PLATFORM_LOOK[item.platform];
          const health = HEALTH_LOOK[item.status];
          const pulse = item.pulse.status ? CHECK_LOOK[item.pulse.status as CheckStatus] : null;
          return (
            <div key={item.platform} className={clsx("relative overflow-hidden rounded-3xl border bg-white/[0.06] p-7 backdrop-blur", item.status === "healthy" ? "border-white/10" : "border-rose-500/70 shadow-[0_0_60px_-15px_rgba(244,63,94,0.7)]")}>
              <div className="flex items-center gap-4">
                <Icon3D icon={look.icon} tone={look.tone} size="xl" />
                <p className="flex-1 font-[Gilroy-Bold] text-[28px]">{look.label}</p>
                <span className="relative inline-flex h-6 w-6">
                  {item.status !== "healthy" ? <span className={clsx("pulse-ring absolute inset-0 rounded-full", TONES[health.tone].text)} /> : null}
                  <span className={clsx("relative h-6 w-6 rounded-full", TONES[health.tone].dot)} />
                </span>
              </div>
              <p className={clsx("mt-5 font-[Gilroy-Bold] text-[38px] leading-none", TONES[health.tone].text)}>{health.label}</p>
              <div className="mt-6 grid grid-cols-3 gap-4 font-[Gilroy-Medium] text-[14px] text-white/60">
                <div><p>Errors</p><p className="font-[Gilroy-Bold] text-[26px] text-white">{item.errorRate}%</p></div>
                <div><p>Alerts</p><p className={clsx("font-[Gilroy-Bold] text-[26px]", item.openAlerts ? "text-rose-300" : "text-white")}>{item.openAlerts}</p></div>
                <div><p>Jobs</p><p className={clsx("font-[Gilroy-Bold] text-[26px]", item.jobs.bad ? "text-rose-300" : "text-white")}>{item.jobs.total ? `${item.jobs.total - item.jobs.bad}/${item.jobs.total}` : "—"}</p></div>
              </div>
              <p className="mt-5 font-[Gilroy-Medium] text-[15px] text-white/60">Server pulse: <span className="text-white">{pulse?.label ?? (item.pulse.configured ? "waiting" : "not set up")}</span></p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
